import { useSelector } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";
import { customGetAPI } from "services/utils/CustomAPI";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import * as Mui from "@mui/material";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { Box, Typography } from "@mui/material";
import { CustomTextBox } from "components/CustomComponents";
import { tooltipStyleProps } from "services/constants/Styles";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { cardBorder } from "services/constants";
import { Check, Edit, InfoOutlined } from "@mui/icons-material";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { rgxWithDot, onlyNumberRegex } from "services/constants/patterns";
export const OffDayPostApproval = ({
  attendanceDetails,
  domain,
  setOpenSidePeek,
  reCallApiOffDayDuty = null,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const {
    control,
    setValue,
    handleSubmit,
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      pay_cycle: "",
      payout_policy: "",
      reason: "",
      durationHrs: null,
      button_loader: false,
    },
  });
  const [offdayDetails, setOffdayDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [bandRows, setBandRows] = useState([]);
  const [editingBandId, setEditingBandId] = useState(null);
  const [bandRowErrors, setBandRowErrors] = useState({});
  const [savingBandId, setSavingBandId] = useState(null);
  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    reset();
  };

  const buildBandRows = useCallback((bands = []) => {
    const normalizeBandHours = (value) => {
      if (!value || typeof value !== "string") return "";
      const timeParts = value.split(":");
      if (timeParts.length < 2) return value;
      return `${timeParts[0]}:${timeParts[1]}`;
    };

    if (!Array.isArray(bands)) return [];

    return bands.map((band, index) => ({
      id: band?.id ?? index,
      band_name: band?.band_name ?? `band ${index + 1}`,
      band_factor:
        band?.band_factor === null || band?.band_factor === undefined
          ? ""
          : String(band?.band_factor),
      band_hours: normalizeBandHours(
        band?.final_approved_durations ?? band?.band_hours ?? band?.hours ?? "",
      ),
    }));
  }, []);

  const sumBandHours = useCallback((rows = []) => {
    if (!Array.isArray(rows) || rows.length === 0) return "";

    let totalMinutes = 0;
    let hasValue = false;

    rows.forEach((row) => {
      const value = row?.band_hours;
      if (!value || typeof value !== "string") return;

      const [hh, mm] = value.split(":");
      const hours = parseInt(hh, 10);
      const minutes = parseInt(mm, 10);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return;

      totalMinutes += hours * 60 + minutes;
      hasValue = true;
    });

    if (!hasValue) return "";

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    return `${String(totalHours).padStart(2, "0")}:${String(
      remainingMinutes,
    ).padStart(2, "0")}`;
  }, []);

  const getBandApiConfig = useCallback(
    (dayType, approvalId, bandId) => {
      const normalizedDayType = String(dayType || "")
        .trim()
        .toLowerCase();

      if (normalizedDayType === "week off" || normalizedDayType === "weekoff") {
        return {
          listUrl: `${domain}team_zone/approve/day_off/teamzone-weekoff-band-factor/?post_weekoff_approval_id=${approvalId}&company_id=${requestCompanyId}`,
          updateUrl: `${domain}team_zone/approve/day_off/teamzone-weekoff-band-factor/${bandId}/?company_id=${requestCompanyId}`,
          approvalKey: "post_weekoff_approval",
        };
      }

      if (normalizedDayType === "holiday") {
        return {
          listUrl: `${domain}team_zone/approve/day_off/teamzone-holiday-band-factor/?post_holiday_approval_id=${approvalId}&company_id=${requestCompanyId}`,
          updateUrl: `${domain}team_zone/approve/day_off/teamzone-holiday-band-factor/${bandId}/?company_id=${requestCompanyId}`,
          approvalKey: "post_holiday_approval",
        };
      }

      return null;
    },
    [domain, requestCompanyId],
  );

  const fetchOffdayDetails = useCallback(async () => {
    if (!attendanceDetails?.date || !attendanceDetails?.user_id || !domain)
      return;

    const date = attendanceDetails?.date?.split("-").reverse().join("-");
    const res = await customGetAPI(
      `${domain}team_zone/approve/day_off/teamzone-particular-user-dayoff/?user_id=${attendanceDetails?.user_id}&date=${date}&company_id=${requestCompanyId}`,
    );

    if (res?.ok) {
      const dateValidation = res?.responseDetails;
      setOffdayDetails(dateValidation ? dateValidation[0] : []);
      setIsUpdate(true);
      setLoading(false);
    }
  }, [attendanceDetails?.date, attendanceDetails?.user_id, domain, requestCompanyId]);

  const fetchOffdayBandRows = useCallback(
    async (approvalId, dayType) => {
      const apiConfig = getBandApiConfig(dayType, approvalId, "");

      if (!domain || !approvalId || !apiConfig?.listUrl) {
        setBandRows([]);
        setEditingBandId(null);
        setBandRowErrors({});
        setSavingBandId(null);
        return;
      }

      const response = await customGetAPI(apiConfig.listUrl);

      if (response?.ok) {
        setBandRows(buildBandRows(response?.responseDetails ?? []));
      } else {
        setBandRows([]);
      }

      setEditingBandId(null);
      setBandRowErrors({});
      setSavingBandId(null);
    },
    [buildBandRows, domain, getBandApiConfig],
  );

  useEffect(() => {
    if (attendanceDetails?.date && attendanceDetails?.user_id) {
      fetchOffdayDetails();
    }
  }, [attendanceDetails?.date, attendanceDetails?.user_id, fetchOffdayDetails]);

  useEffect(() => {
    if (offdayDetails && isUpdate) {
      if (
        offdayDetails?.based_on === "Hours" ||
        offdayDetails?.based_on === "Equivalent Hours"
      ) {
        const duration =
          offdayDetails?.approved_qty?.slice(0, 5) ||
          offdayDetails?.credited_qty?.slice(0, 5);
        setValue(
          "durationHrs",
          duration ? dayjs(duration, "HH:mm", true) : null,
        );
      } else if (offdayDetails?.based_on === "Shift") {
        setValue(
          "no_of_shifts",
          offdayDetails?.approved_qty ?? offdayDetails?.credited_qty,
        );
      } else {
        const approvedQty = parseInt(offdayDetails?.approved_qty);
        setValue(
          "amount",
          Number.isNaN(approvedQty)
            ? parseInt(offdayDetails?.credited_qty)
            : approvedQty,
        );
      }
      setValue("payout_policy", offdayDetails?.payout_policy);
      setEditingBandId(null);
      setBandRowErrors({});
      setIsUpdate(false);
    }
  }, [buildBandRows, isUpdate, offdayDetails, setValue, watch]);

  useEffect(() => {
    fetchOffdayBandRows(offdayDetails?.approval_id, offdayDetails?.day_type);
  }, [
    fetchOffdayBandRows,
    offdayDetails?.approval_id,
    offdayDetails?.day_type,
  ]);

  const InfoRow = ({ label, value, tooltip }) => (
    <Mui.Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      <Mui.Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Mui.Typography sx={{ fontWeight: 600, color: "#757575" }}>
          {label}
        </Mui.Typography>

        {tooltip && (
          <Mui.Tooltip title={tooltip} arrow slotProps={tooltipStyleProps}>
            <InfoOutlined
              sx={{ fontSize: 18, color: "#9e9e9e", cursor: "pointer" }}
            />
          </Mui.Tooltip>
        )}
      </Mui.Box>

      <Mui.Typography sx={{ fontWeight: 600 }}>{value}</Mui.Typography>
    </Mui.Box>
  );

  const handleStatusUpdated = (response) => {
    setValue("button_loader", false);
    if (typeof reCallApiOffDayDuty === "function") {
      reCallApiOffDayDuty(true);
    }
    setOpenSidePeek(false);
    showToast("success", response?.responseDetails?.message);
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}team_zone/approve/day_off/teamzone-post-day-off/?company_id=${requestCompanyId}`,
      "PUT",
      {
        approval_id: offdayDetails?.approval_id,
        approval_type: offdayDetails?.day_type,
        payout_policy: data?.payout_policy,
        status: "Approved",
        ...((offdayDetails?.based_on === "Hours" ||
          offdayDetails?.based_on === "Equivalent Hours") && {
          approved_qty: dayjs(data?.durationHrs).format("HH:mm"),
        }),
        ...(offdayDetails?.based_on === "Shift" && {
          approved_qty: data?.no_of_shifts,
        }),
        ...(offdayDetails?.based_on === "Amount" && {
          approved_qty: data?.amount,
        }),
      },
    );
    if (response?.ok) {
      handleStatusUpdated(response);
    } else {
      // On failure customAPI already surfaces the error toast.
      setValue("button_loader", false);
    }
  };

  const handleReject = async () => {
    setValue("button_loader", true);
    const response = await customAPI(
      `${domain}team_zone/approve/day_off/teamzone-post-day-off/?company_id=${requestCompanyId}`,
      "PUT",
      {
        approval_id: offdayDetails?.approval_id,
        approval_type: offdayDetails?.day_type,
        status: "Rejected",
        rejected_reason: watch("reason"),
      },
    );
    if (response?.ok) {
      handleStatusUpdated(response);
    } else {
      // On failure customAPI already surfaces the error toast.
      setValue("button_loader", false);
    }
  };

  const updateBandHours = (bandId, value) => {
    setBandRows((prev) =>
      prev.map((row) =>
        row.id === bandId
          ? {
              ...row,
              band_hours: value,
            }
          : row,
      ),
    );
    setBandRowErrors((prev) => ({
      ...prev,
      [bandId]: {
        ...prev[bandId],
        band_hours: value ? "" : prev[bandId]?.band_hours,
      },
    }));
  };

  const validateBandRow = (band) => {
    const nextErrors = {
      band_hours: band?.band_hours ? "" : "Value is required",
    };

    setBandRowErrors((prev) => ({
      ...prev,
      [band.id]: nextErrors,
    }));

    return !nextErrors.band_hours;
  };

  const handleBandEditToggle = async (band) => {
    if (editingBandId === band.id) {
      if (!validateBandRow(band)) return;
      setSavingBandId(band.id);
      const approvedDuration = band.band_hours ? `${band.band_hours}:00` : "";
      const apiConfig = getBandApiConfig(
        offdayDetails?.day_type,
        offdayDetails?.approval_id,
        band.id,
      );

      if (!apiConfig?.updateUrl || !apiConfig?.approvalKey) {
        setSavingBandId(null);
        return;
      }

      const response = await customAPI(apiConfig.updateUrl, "PUT", {
        band_factor: parseFloat(band.band_factor),
        final_approved_durations: approvedDuration,
        [apiConfig.approvalKey]: offdayDetails?.approval_id,
      });

      if (response?.ok) {
        await fetchOffdayBandRows(
          offdayDetails?.approval_id,
          offdayDetails?.day_type,
        );
        await fetchOffdayDetails();
        showToast("success", response?.responseDetails?.message);
        setEditingBandId(null);
      } else {
        setSavingBandId(null);
      }

      return;
    }

    setBandRowErrors((prev) => ({
      ...prev,
      [band.id]: {
        band_hours: "",
      },
    }));
    setEditingBandId(band.id);
  };

  const handleReason = (event) => {
    const reason = event.target.value;
    if (reason) {
      setValue("reason", event.target.value);
    } else {
      setValue("reason", "");
    }
  };

  const updateNumberOfShifs = (event) => {
    if (event.target.value && rgxWithDot.test(event.target.value)) {
      setValue("no_of_shifts", event.target.value);
    } else {
      setValue("no_of_shifts", "");
    }
  };

  const updateAmount = (event) => {
    if (event.target.value && onlyNumberRegex.test(event.target.value)) {
      setValue("amount", event.target.value);
    } else {
      setValue("amount", "");
    }
  };

  const isHoursBased =
    offdayDetails?.based_on === "Hours" ||
    offdayDetails?.based_on === "Equivalent Hours";

  const bandHoursTotal = isHoursBased ? sumBandHours(bandRows) : "";

  const creditedQtyValue =
    bandHoursTotal || offdayDetails?.credited_qty?.slice(0, 5) || "-";

  const approvedQtyValue =
    bandHoursTotal ||
    offdayDetails?.approved_qty ||
    offdayDetails?.credited_qty ||
    "-";

  return (
    <React.Fragment>
      <Mui.Typography fontWeight={600}>
        {offdayDetails?.day_type}
      </Mui.Typography>
      {offdayDetails && !loading && (
        <Mui.Box sx={{ p: 0.2 }}>
          {offdayDetails?.pre_dayoff && (
            <>
              <Mui.Typography
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  letterSpacing: "-0.3px",
                }}
              >
                Pre Authorization
              </Mui.Typography>

              <Mui.Card sx={cardBorder}>
                <Mui.CardContent sx={{ p: 2 }}>
                  <Mui.Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "24px 80px",
                    }}
                  >
                    <InfoRow
                      label="Start Time"
                      value={offdayDetails?.start_time ?? "-"}
                    />
                    <InfoRow
                      label="Preapproved Qty"
                      value={offdayDetails?.preapproved_qty ?? "-"}
                    />
                    <InfoRow
                      label="Duration"
                      value={offdayDetails?.duration ?? "-"}
                    />
                    <InfoRow
                      label="Total Worked"
                      value={offdayDetails?.worked_hours ?? "-"}
                    />
                  </Mui.Box>

                  <Mui.Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Mui.Typography
                      variant="body1"
                      sx={{
                        color: "#4c5c6b",
                      }}
                    >
                      Pre approved by
                    </Mui.Typography>
                    <Mui.Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {offdayDetails?.pre_approved_by ?? "-"}
                    </Mui.Typography>
                    <Mui.Typography
                      variant="body1"
                      sx={{
                        color: "#4c5c6b",
                      }}
                    >
                      On
                    </Mui.Typography>
                    <Mui.Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {offdayDetails?.pre_approved_at ?? "-"}
                    </Mui.Typography>
                  </Mui.Box>
                </Mui.CardContent>
              </Mui.Card>
            </>
          )}

          <Mui.Typography
            sx={{
              fontWeight: 600,
              mb: 1,
              mt: 1,
              letterSpacing: "-0.3px",
            }}
          >
            Approval
          </Mui.Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Mui.Card
              sx={{
                border: "1px solid #c8cbd3",
              }}
            >
              <Mui.CardContent sx={{ p: 3 }}>
                <Mui.Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "24px 80px",
                  }}
                >
                  <InfoRow
                    label="Day Type"
                    value={offdayDetails?.day_type ?? "-"}
                  />
                  <InfoRow
                    label={`${offdayDetails?.day_type} Cal Based on`}
                    value={offdayDetails?.based_on ?? "-"}
                  />
                  <InfoRow
                    label="Worked Hrs"
                    value={offdayDetails?.worked_hours ?? "-"}
                  />
                  <InfoRow
                    label={`${offdayDetails?.day_type} Credited QTY`}
                    value={creditedQtyValue}
                    tooltip="The credited quantity is calculated based on grade hours. The payment factor will be applied during payroll calculation."
                  />
                </Mui.Box>

                <Mui.Box sx={{ mt: 3 }}>
                  <Mui.Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Mui.Typography
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      Approved Qty
                    </Mui.Typography>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: {
                          xs: "100%",
                          md: 405,
                        },
                      }}
                    >
                      {offdayDetails?.based_on === "Hours" ||
                      offdayDetails?.based_on === "Equivalent Hours" ? (
                        <>
                          {bandRows?.length !== 0 && isHoursBased ? (
                            <Typography>{approvedQtyValue} </Typography>
                          ) : (
                            <CustomTimePicker
                              name="durationHrs"
                              control={control}
                              errors={errors}
                              validateFn={(value) => {
                                if (!value || !dayjs(value).isValid()) {
                                  return "Duration is required";
                                }
                                return true;
                              }}
                            />
                          )}
                        </>
                      ) : offdayDetails?.based_on === "Shift" ? (
                        <CustomTextBox
                          selectedValue={watch("no_of_shifts")}
                          rules={{
                            ...register("no_of_shifts", {
                              required: "No of shift is required",
                            }),
                          }}
                          updateDataFunction={updateNumberOfShifs}
                          placeHolder="Enter no of shifts"
                          errorMessage={
                            errors?.no_of_shifts?.message
                              ? errors?.no_of_shifts?.message
                              : ""
                          }
                          textBoxError={errors.no_of_shifts ? true : false}
                        />
                      ) : (
                        <CustomTextBox
                          selectedValue={watch("amount")}
                          rules={{
                            ...register("amount", {
                              required: "Amount is required",
                            }),
                          }}
                          updateDataFunction={updateAmount}
                          placeHolder="Enter amount"
                          errorMessage={
                            errors?.amount?.message
                              ? errors?.amount?.message
                              : ""
                          }
                          textBoxError={errors.amount ? true : false}
                        />
                      )}
                    </Box>
                  </Mui.Box>
                </Mui.Box>

                {(offdayDetails?.based_on === "Hours" ||
                  offdayDetails?.based_on === "Equivalent Hours") &&
                  !!bandRows.length && (
                    <Mui.Box sx={{ mt: 3 }}>
                      <Mui.Box
                        sx={{
                          border: "1px solid #d7dce5",
                          borderRadius: "14px",
                          overflow: "hidden",
                          width: "100%",
                        }}
                      >
                        <Mui.Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(110px, 1.15fr) minmax(90px, 0.85fr) minmax(100px, 0.9fr) 52px",
                            bgcolor: "#f6f8fb",
                            borderBottom: "1px solid #d7dce5",
                          }}
                        >
                          <Mui.Box
                            sx={{
                              px: 1.5,
                              py: 1.25,
                              borderRight: "1px solid #d7dce5",
                            }}
                          >
                            <Mui.Typography
                              sx={{ fontWeight: 500, fontSize: 14 }}
                            >
                              Band Name
                            </Mui.Typography>
                          </Mui.Box>
                          <Mui.Box
                            sx={{
                              px: 1.5,
                              py: 1.25,
                              borderRight: "1px solid #d7dce5",
                            }}
                          >
                            <Mui.Typography
                              sx={{ fontWeight: 500, fontSize: 14 }}
                            >
                              Band Factor
                            </Mui.Typography>
                          </Mui.Box>
                          <Mui.Box
                            sx={{
                              px: 1.5,
                              py: 1.25,
                              borderRight: "1px solid #d7dce5",
                            }}
                          >
                            <Mui.Typography
                              sx={{ fontWeight: 500, fontSize: 14 }}
                            >
                              Band Hours
                            </Mui.Typography>
                          </Mui.Box>
                          <Mui.Box
                            sx={{
                              px: 0.5,
                              py: 1.25,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Mui.Typography
                              sx={{ fontWeight: 500, fontSize: 14 }}
                            >
                              Edit
                            </Mui.Typography>
                          </Mui.Box>
                        </Mui.Box>

                        {bandRows.map((band, rowIndex) => (
                          <Mui.Box
                            key={band.id}
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "minmax(110px, 1.15fr) minmax(90px, 0.85fr) minmax(100px, 0.9fr) 52px",
                              borderBottom:
                                rowIndex === bandRows.length - 1
                                  ? "none"
                                  : "1px solid #d7dce5",
                            }}
                          >
                            <Mui.Box
                              sx={{
                                px: 1.5,
                                py: 1.25,
                                display: "flex",
                                alignItems: "center",
                                borderRight: "1px solid #d7dce5",
                              }}
                            >
                              <Mui.Typography
                                sx={{
                                  fontSize: { xs: 14, md: 16 },
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {band.band_name}
                              </Mui.Typography>
                            </Mui.Box>
                            <Mui.Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRight: "1px solid #d7dce5",
                              }}
                            >
                              <Mui.Typography sx={{ fontSize: 15, py: 1 }}>
                                {band.band_factor || "-"}
                              </Mui.Typography>
                            </Mui.Box>
                            <Mui.Box
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRight: "1px solid #d7dce5",
                              }}
                            >
                              {editingBandId === band.id ? (
                                <LocalizationProvider
                                  dateAdapter={AdapterDayjs}
                                >
                                  <TimePicker
                                    ampm={false}
                                    viewRenderers={{
                                      hours: renderTimeViewClock,
                                      minutes: renderTimeViewClock,
                                      seconds: renderTimeViewClock,
                                    }}
                                    views={["hours", "minutes"]}
                                    openTo="hours"
                                    value={
                                      band.band_hours
                                        ? dayjs(`2000-01-01T${band.band_hours}`)
                                        : null
                                    }
                                    onChange={(newValue) =>
                                      updateBandHours(
                                        band.id,
                                        newValue && dayjs(newValue).isValid()
                                          ? dayjs(newValue).format("HH:mm")
                                          : "",
                                      )
                                    }
                                    slotProps={{
                                      textField: {
                                        size: "small",
                                        error: Boolean(
                                          bandRowErrors[band.id]?.band_hours,
                                        ),
                                        helperText:
                                          bandRowErrors[band.id]?.band_hours ||
                                          "",
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              ) : (
                                <Mui.Typography sx={{ fontSize: 15, py: 1 }}>
                                  {band.band_hours || "--:--"}
                                </Mui.Typography>
                              )}
                            </Mui.Box>
                            <Mui.Box
                              sx={{
                                px: 0.25,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Mui.IconButton
                                size="small"
                                onClick={() => handleBandEditToggle(band)}
                                disabled={
                                  savingBandId === band.id ||
                                  offdayDetails?.status === "Approved" ||
                                  offdayDetails?.status === "Rejected"
                                }
                                sx={{ color: "#5f6368" }}
                              >
                                {editingBandId === band.id ? (
                                  <Check fontSize="small" />
                                ) : (
                                  <Edit fontSize="small" />
                                )}
                              </Mui.IconButton>
                            </Mui.Box>
                          </Mui.Box>
                        ))}
                      </Mui.Box>
                    </Mui.Box>
                  )}
                <Mui.Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Mui.Typography
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Add payout to
                  </Mui.Typography>
                  <Mui.RadioGroup
                    row
                    value={watch("payout_policy")}
                    onChange={(e) => setValue("payout_policy", e.target.value)}
                    sx={{ gap: "20px" }}
                  >
                    <Mui.FormControlLabel
                      value="Next Payrun"
                      control={<Mui.Radio />}
                      label="Next Payrun"
                    />
                    <Mui.FormControlLabel
                      value="Backlog"
                      control={<Mui.Radio />}
                      label="Backlog"
                    />
                  </Mui.RadioGroup>
                </Mui.Box>
                {offdayDetails?.status !== "Approved" &&
                  offdayDetails?.status !== "Rejected" && (
                    <Mui.Box
                      sx={{
                        mt: 3,
                        display: "flex",
                        justifyContent: "end",
                        gap: 1,
                      }}
                    >
                      <CustomButtonWithBorder
                        actionName="Reject"
                        handleColor={true}
                        typeName="button"
                        actionFuntion={() => setShowConfirmationDialog(true)}
                        disableAction={watch("button_loader")}
                      />

                      <CustomButtonWithBorder
                        actionName="Approve"
                        typeName="submit"
                        disableAction={watch("button_loader")}
                      />
                    </Mui.Box>
                  )}
              </Mui.CardContent>
            </Mui.Card>
            <ConfirmationDialog
              open={showConfirmationDialog}
              onClose={handleCancelClose}
              onConfirm={() => {
                handleReject();
              }}
              title={"Reason"}
              confirmText={"Reject"}
              cancelText="Cancel"
              showChildren={true}
            >
              <Box sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Reason (Optional)
                </Typography>
                {showConfirmationDialog && (
                  <CustomTextBox
                    type="multiline"
                    rules={{
                      ...register("reason", {
                        required: "Reject reason is required",
                      }),
                    }}
                    selectedValue={watch("reason")}
                    updateDataFunction={handleReason}
                    placeHolder={"Enter a reason for reject"}
                  />
                )}
              </Box>
            </ConfirmationDialog>
          </form>
        </Mui.Box>
      )}
    </React.Fragment>
  );
};
